import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { useClerkReady } from "@/components/auth/ClerkAuthProvider";
import { LogOut } from "lucide-react";
import { clearStaffSession, hasStaffSession } from "@/lib/staff";
import { API } from "@/lib/api";
import axios from "axios";
import { FileText, LayoutDashboard, Loader2, Map, ShieldAlert, Users, UserRoundSearch } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { useApi } from "@/lib/api";
import { PushToggle } from "@/pages/admin/PushToggle";

const LINKS = [
  { to: "/admin", label: "Panoramica", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/admin/lead", label: "Lead & CRM", icon: UserRoundSearch, id: "leads" },
  { to: "/admin/mappa", label: "Mappa di calore", icon: Map, id: "heatmap" },
  { to: "/admin/preventivi", label: "Preventivi", icon: FileText, id: "quotes" },
  { to: "/admin/utenti", label: "Utenti", icon: Users, id: "users" },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 whitespace-nowrap rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-slate-800 text-white" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"}`;

const Pending = ({ email }) => (
  <div data-testid="admin-pending" className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 p-6 text-center">
    <ShieldAlert className="h-10 w-10 text-amber-500" />
    <h1 className="text-2xl font-semibold">Accesso in attesa di approvazione</h1>
    <p className="max-w-md text-sm text-slate-600">L'account {email} non ha ancora i permessi di amministratore. Chiedi a un admin di abilitarti dalla sezione Utenti.</p>
    <div className="flex items-center gap-4">
      <UserButton />
      <NavLink to="/" data-testid="pending-back-link" className="text-sm font-medium text-slate-600 hover:text-slate-900">Torna al sito</NavLink>
    </div>
  </div>
);

const Account = ({ staff }) => {
  if (!staff) return <UserButton />;
  return (
    <button data-testid="staff-logout-button" aria-label="Esci" title="Esci"
      onClick={() => {
        clearStaffSession();
        axios.post(`${API}/staff/logout`, {}, { withCredentials: true })
          .catch((err) => console.warn("Logout staff:", err?.message))
          .finally(() => Promise.resolve(window.Clerk?.signOut?.()).finally(() => window.location.assign("/sign-in")));
      }}
      className="grid h-8 w-8 place-items-center rounded-full border border-slate-700 text-slate-300 hover:border-emerald-500 hover:text-white">
      <LogOut className="h-4 w-4" />
    </button>
  );
};

function AdminShell() {
  const api = useApi();
  const [me, setMe] = useState(null);
  useEffect(() => {
    api("get", "/me").then(setMe).catch(() => setMe({ role: "error" }));
  }, [api]);

  if (!me) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (me.role !== "admin") return <Pending email={me.email} />;
  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      <aside className="bg-[#0B0F17] lg:fixed lg:inset-y-0 lg:w-64">
        <div className="flex items-center justify-between px-5 py-5 lg:block">
          <NavLink to="/" data-testid="admin-home-link"><Logo dark /></NavLink>
          <div className="lg:hidden"><Account staff={me.staff} /></div>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:pb-0">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={linkCls} data-testid={`admin-nav-${l.id}`}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 pb-3 pt-2 lg:mt-4 lg:border-t lg:border-slate-800 lg:pt-4"><PushToggle /></div>
        <div className="absolute bottom-6 left-5 hidden items-center gap-3 lg:flex">
          <Account staff={me.staff} />
          <span className="max-w-[160px] truncate text-xs text-slate-400">{me.email}</span>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-5 sm:p-8 lg:ml-64 lg:p-10"><Outlet /></main>
    </div>
  );
}

export default function AdminLayout() {
  const clerkReady = useClerkReady();
  if (hasStaffSession()) return <AdminShell />;
  if (!clerkReady) return <Navigate to="/sign-in" replace />;
  return (
    <>
      <Show when="signed-in"><AdminShell /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
    {children}
  </div>
);
